/**
 * Comprehensive Sources Management
 * Full implementation of all Source API endpoints
 */

import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card } from '../UI/Card';
import { Button } from '../UI/Button';
import { Input } from '../UI/Input';
import { Badge } from '../UI/Badge';
import { 
  listSources, 
  deleteSource, 
  triggerSync, 
  getSyncStatus,
  triggerFileUpload,
} from '../../api/sources';
import { getJob } from '../../api/jobs';
import { useWorkspace } from '../../hooks/useWorkspace';
import { AddSourceModal } from './AddSourceModal';
import { BulkUploadModal } from './BulkUploadModal';
import { DeleteSourceConfirmationModal } from './DeleteSourceConfirmationModal';
import { SourceFileTabs } from './SourceFileTabs';
import { 
  formatFileSize,
  buildFileTree,
  SUPPORTED_EXTENSIONS,
  EXTENSION_DESCRIPTIONS,
  type FileNode,
} from './sourceUtils';
import { 
  Database, 
  Plus, 
  RefreshCw, 
  Search, 
  Filter, 
  ChevronLeft, 
  ChevronRight,
  Trash2,
  Play,
  Activity,
  AlertCircle,
  CheckCircle,
  Clock,
  Folder,
  Cloud,
  File,
  ChevronDown,
  ChevronRight as ChevronRightIcon,
} from 'lucide-react';

// Component to display artifact statistics
function SourceArtifactStats({ sourceId, sourceType }: { sourceId: string; sourceType: string }) {
  const { data: syncStatus, isLoading } = useQuery({
    queryKey: ['syncStatus', sourceId],
    queryFn: () => getSyncStatus(sourceId),
    enabled: true,
    refetchInterval: 60000, // Refresh every minute
  });

  const handleNavigateToKnowledgeBase = () => {
    // Navigate to Knowledge Base with source filter
    const url = `/knowledge?source_id=${sourceId}`;
    window.dispatchEvent(new CustomEvent('navigate', { detail: { path: url } }));
  };

  if (isLoading) {
    return (
      <div className="mt-3 pt-3 border-t">
        <div className="text-xs text-gray-500 dark:text-gray-400">Loading stats...</div>
      </div>
    );
  }

  const artifactStats = syncStatus?.metadata?.artifact_stats;
  if (!artifactStats) {
    return null;
  }

  const total = artifactStats.total_artifacts || 0;
  const chunked = artifactStats.artifacts_with_chunks || 0;
  const entities = artifactStats.artifacts_with_entities || 0;
  const chunkedPct = artifactStats.chunked_percent || 0;
  const entitiesPct = artifactStats.entities_percent || 0;

  if (total === 0) {
    return null;
  }

  return (
    <div 
      className="mt-3 pt-3 border-t cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/50 rounded-lg p-2 -m-2 transition-colors"
      onClick={handleNavigateToKnowledgeBase}
      title="Click to view artifacts in Knowledge Base"
    >
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
        <div>
          <div className="text-gray-500 dark:text-gray-400 text-xs">Artifacts</div>
          <div className="font-semibold text-gray-900 dark:text-gray-100 text-lg">{total}</div>
        </div>
        <div>
          <div className="text-gray-500 dark:text-gray-400 text-xs">Parsed</div>
          <div className="font-semibold text-gray-900 dark:text-gray-100 text-lg">
            {chunked} ({chunkedPct}%)
          </div>
        </div>
        <div>
          <div className="text-gray-500 dark:text-gray-400 text-xs">With Entities</div>
          <div className="font-semibold text-gray-900 dark:text-gray-100 text-lg">
            {entities} ({entitiesPct}%)
          </div>
        </div>
        <div>
          <div className="text-gray-500 dark:text-gray-400 text-xs">Processing</div>
          <div className="font-semibold text-gray-900 dark:text-gray-100 text-lg">
            {total - chunked > 0 ? (
              <span className="text-yellow-600 dark:text-yellow-400">{total - chunked} pending</span>
            ) : (
              <span className="text-green-600 dark:text-green-400">Complete</span>
            )}
          </div>
        </div>
      </div>
      <div className="text-xs text-gray-400 dark:text-gray-500 mt-2 italic">
        Click to view artifacts in Knowledge Base →
      </div>
    </div>
  );
}


export function SourceList() {
  const { projectId } = useWorkspace();
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState<string>('all');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [filterParsed, setFilterParsed] = useState<string>('all'); // 'all', 'parsed', 'not_parsed'
  const [parsedThreshold, setParsedThreshold] = useState<number>(1); // Threshold percentage for "not parsed"
  const [sortBy, setSortBy] = useState<'description' | 'type' | 'last_sync_at'>('last_sync_at');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [page, setPage] = useState(0);
  const [pageSize] = useState(10);
  const [showAddSource, setShowAddSource] = useState(false);
  const [sourceType, setSourceType] = useState<'database' | 'local_folder' | 'sharepoint' | 'gdrive'>('database');
  const [expandedSources, setExpandedSources] = useState<Set<string>>(new Set());
  const [sourceViewTabs, setSourceViewTabs] = useState<Record<string, 'list' | 'tree' | 'stats'>>({});
  const [sourceExpandedPaths, setSourceExpandedPaths] = useState<Record<string, Set<string>>>({});
  const [bulkUploadSource, setBulkUploadSource] = useState<{ id: string; name: string; metadata?: Record<string, any> } | null>(null);
  const [fileListPages, setFileListPages] = useState<Record<string, number>>({});
  const [fileListPageSize] = useState(50);
  const [deleteConfirmSource, setDeleteConfirmSource] = useState<{ id: string; name: string; artifactCount: number } | null>(null);
  const [uploadProgress, setUploadProgress] = useState<Record<string, {
    jobId: string;
    status: string;
    itemsScanned?: number;
    filesFound?: number;
    filesQueued?: number;
    elapsedSeconds?: number;
    message?: string;
  }>>({});

  const { data: sources, isLoading, refetch } = useQuery({
    queryKey: ['sources', projectId],
    queryFn: () => listSources(projectId!, { 
      artifact_ids_limit: 50,  // Default: only fetch 50 artifact IDs for performance
      include_all_artifact_ids: false 
    }),
    enabled: !!projectId,
    refetchInterval: 30000,
  });

  const deleteMutation = useMutation({
    mutationFn: (sourceId: string) => deleteSource(sourceId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sources', projectId] });
    },
  });

  const syncMutation = useMutation({
    mutationFn: ({ sourceId, tableName }: { sourceId: string; tableName?: string }) => 
      triggerSync(sourceId, tableName),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sources', projectId] });
    },
  });

  // Pre-filter by search, type, and status (before parsed filter to reduce API calls)
  const preFilteredSources = React.useMemo(() => {
    if (!sources) return [];
    return sources.filter((source) => {
      const matchesSearch = 
        source.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        source.type?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        source.id?.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesType = filterType === 'all' || source.type === filterType;
      const matchesStatus = filterStatus === 'all' || source.sync_status === filterStatus;
      return matchesSearch && matchesType && matchesStatus;
    });
  }, [sources, searchTerm, filterType, filterStatus]);

  // Fetch sync status only for pre-filtered sources when parsed filter is active
  const sourceIdsForParsedFilter = React.useMemo(() => 
    filterParsed !== 'all' ? preFilteredSources.map(s => s.id) : [],
    [preFilteredSources, filterParsed]
  );

  const syncStatusQueries = useQuery({
    queryKey: ['syncStatuses', sourceIdsForParsedFilter.sort().join(',')],
    queryFn: async () => {
      if (sourceIdsForParsedFilter.length === 0) return {};
      const statuses: Record<string, any> = {};
      // Batch requests with Promise.allSettled to handle errors gracefully
      const results = await Promise.allSettled(
        sourceIdsForParsedFilter.map(async (id) => {
          try {
            const status = await getSyncStatus(id);
            return { id, status };
          } catch (error) {
            console.warn(`Failed to fetch sync status for source ${id}:`, error);
            return { id, status: null };
          }
        })
      );
      results.forEach((result) => {
        if (result.status === 'fulfilled' && result.value.status) {
          statuses[result.value.id] = result.value.status;
        }
      });
      return statuses;
    },
    enabled: filterParsed !== 'all' && sourceIdsForParsedFilter.length > 0,
    staleTime: 60000, // Cache for 1 minute
  });

  const filteredSources = React.useMemo(() => {
    if (!preFilteredSources) return [];
    
    let filtered = preFilteredSources.filter((source) => {
      // Filter by parsed percentage if enabled
      if (filterParsed !== 'all') {
        const syncStatus = syncStatusQueries.data?.[source.id];
        const artifactStats = syncStatus?.metadata?.artifact_stats;
        
        if (artifactStats && artifactStats.total_artifacts > 0) {
          const parsedPct = artifactStats.chunked_percent || 0;
          if (filterParsed === 'parsed') {
            return parsedPct >= parsedThreshold;
          } else if (filterParsed === 'not_parsed') {
            return parsedPct < parsedThreshold;
          }
        } else {
          // If no stats available, exclude from "parsed" filter, include in "not_parsed"
          return filterParsed === 'not_parsed';
        }
      }
      return true;
    }).sort((a, b) => {
      const aVal = a[sortBy] ?? '';
      const bVal = b[sortBy] ?? '';
      
      if (sortOrder === 'asc') {
        return aVal > bVal ? 1 : -1;
      } else {
        return aVal < bVal ? 1 : -1;
      }
    });
    
    return filtered;
  }, [preFilteredSources, filterParsed, parsedThreshold, sortBy, sortOrder, syncStatusQueries.data]);

  const paginatedSources = React.useMemo(() => {
    const start = page * pageSize;
    return filteredSources.slice(start, start + pageSize);
  }, [filteredSources, page, pageSize]);

  const totalPages = Math.ceil(filteredSources.length / pageSize);

  // Build file tree using utility function
  const buildFileTreeMemo = useCallback((files: FileNode[]) => buildFileTree(files), []);

  // Initialize expanded paths for a source when it's expanded
  useEffect(() => {
    sources?.forEach((source) => {
      if (source.type === 'local_folder' && source.files && source.files.length > 0 && expandedSources.has(source.id)) {
        // Initialize tab if not set
        if (!sourceViewTabs[source.id]) {
          setSourceViewTabs(prev => ({ ...prev, [source.id]: 'list' }));
        }

        // Initialize expanded paths if not set (expand all by default)
        if (!sourceExpandedPaths[source.id]) {
          const tree = buildFileTreeMemo(source.files);
          const allPaths = new Set<string>();
          const getAllPaths = (nodes: any[]) => {
            nodes.forEach(node => {
              if (node.path) allPaths.add(node.path);
              if (node.name) allPaths.add(node.name);
              if (node.children) getAllPaths(node.children);
            });
          };
          getAllPaths(tree);
          setSourceExpandedPaths(prev => ({ ...prev, [source.id]: allPaths }));
        }
      }
    });
  }, [sources, expandedSources, buildFileTreeMemo, sourceViewTabs, sourceExpandedPaths]);

  // Initialize file tree expansion when source is expanded
  useEffect(() => {
    if (!sources) return;
    
    sources.forEach(source => {
      if (expandedSources.has(source.id) && source.type === 'local_folder') {
        const allFilesFromMetadata = source.metadata?.files || [];
        if (allFilesFromMetadata.length > 0) {
          const tree = buildFileTreeMemo(allFilesFromMetadata);
          if (!sourceExpandedPaths[source.id] || sourceExpandedPaths[source.id].size === 0) {
            const allPaths = new Set<string>();
            const collectPaths = (nodes: any[]) => {
              nodes.forEach(node => {
                if (node.path) allPaths.add(node.path);
                if (node.name) allPaths.add(node.name);
                if (node.children && node.children.length > 0) {
                  collectPaths(node.children);
                }
              });
            };
            collectPaths(tree);
            setSourceExpandedPaths(prev => ({ ...prev, [source.id]: allPaths }));
          }
        }
      }
    });
  }, [expandedSources, sources, buildFileTreeMemo, sourceExpandedPaths]);

  const handleDelete = (sourceId: string, description: string, artifactCount: number) => {
    setDeleteConfirmSource({ id: sourceId, name: description, artifactCount });
  };

  const confirmDelete = async () => {
    if (!deleteConfirmSource) return;
    await deleteMutation.mutateAsync(deleteConfirmSource.id);
    setDeleteConfirmSource(null);
  };

  const handleTriggerSync = async (sourceId: string, sourceType?: string, sourceMetadata?: Record<string, any>, sourceName?: string) => {
    // For local_folder sources, check if it's client-side
    if (sourceType === 'local_folder') {
      const isClientSide = sourceMetadata?.client_side ?? false;
      if (isClientSide) {
        // Open bulk upload modal for client-side sources
        setBulkUploadSource({
          id: sourceId,
          name: sourceName || sourceId,
          metadata: sourceMetadata,
        });
        return;
      }
      
      // Server-side sources can use the upload endpoint
      try {
        const response = await triggerFileUpload(sourceId);
        
        // Start polling job status for progress updates
        const jobId = response.job_id;
        setUploadProgress(prev => ({
          ...prev,
          [sourceId]: {
            jobId,
            status: 'scanning',
            itemsScanned: 0,
            filesFound: 0,
            filesQueued: 0,
            message: 'File scanning in progress...',
          }
        }));
        
        // Poll job status every 2 seconds
        const pollInterval = setInterval(async () => {
          try {
            const job = await getJob(jobId);
            const inputData = job.input_data || {};
            const scanStatus = inputData.scan_status;
            const scanProgress = inputData.scan_progress || {};
            const files = inputData.files || [];
            
            setUploadProgress(prev => ({
              ...prev,
              [sourceId]: {
                jobId,
                status: job.status || 'unknown',
                itemsScanned: scanProgress.items_scanned || 0,
                filesFound: scanProgress.files_found || files.length || 0,
                filesQueued: files.length || 0,
                elapsedSeconds: scanProgress.elapsed_seconds,
                message: scanStatus === 'scanning' 
                  ? `Scanning... ${scanProgress.items_scanned?.toLocaleString() || 0} items, ${scanProgress.files_found?.toLocaleString() || 0} files found`
                  : scanStatus === 'complete'
                  ? `Scan complete: ${files.length.toLocaleString()} files queued for upload`
                  : job.status === 'queued'
                  ? `Upload queued: ${files.length.toLocaleString()} files`
                  : job.status === 'running'
                  ? `Uploading files...`
                  : job.status === 'completed'
                  ? `Upload complete!`
                  : job.status === 'failed'
                  ? `Upload failed: ${job.error_message || 'Unknown error'}`
                  : 'Processing...',
              }
            }));
            
            // Stop polling if job is complete or failed
            if (['completed', 'failed'].includes(job.status || '')) {
              clearInterval(pollInterval);
              queryClient.invalidateQueries({ queryKey: ['sources', projectId] });
              // Clear progress after 5 seconds
              setTimeout(() => {
                setUploadProgress(prev => {
                  const newProgress = { ...prev };
                  delete newProgress[sourceId];
                  return newProgress;
                });
              }, 5000);
            }
          } catch (error) {
            console.error('Error polling job status:', error);
            // Continue polling on error
          }
        }, 2000);
        
        // Clear interval after 10 minutes (safety timeout)
        setTimeout(() => clearInterval(pollInterval), 600000);
        
      } catch (error: any) {
        const errorMsg = error.response?.data?.detail || error.message || 'Failed to trigger file upload';
        alert(`Error: ${errorMsg}`);
      }
    } else if (sourceType === 'sharepoint' || sourceType === 'gdrive') {
      // For SharePoint/GDrive sources, trigger sync and poll for progress
      try {
        const response = await triggerSync(sourceId);
        
        // Start polling job status for progress updates
        const jobId = response.job_id;
        setUploadProgress(prev => ({
          ...prev,
          [sourceId]: {
            jobId,
            status: 'queued',
            message: 'Sync queued successfully',
          }
        }));
        
        // Poll job status every 2 seconds
        const pollInterval = setInterval(async () => {
          try {
            const job = await getJob(jobId);
            const inputData = job.input_data || {};
            const outputData = job.output_data || {};
            
            setUploadProgress(prev => ({
              ...prev,
              [sourceId]: {
                jobId,
                status: job.status || 'unknown',
                message: job.status === 'queued'
                  ? 'Sync queued...'
                  : job.status === 'running'
                  ? `Syncing... ${inputData.processed_files || 0}/${inputData.total_files || 0} files`
                  : job.status === 'completed'
                  ? `Sync complete! ${outputData.artifacts_created || 0} files synced`
                  : job.status === 'failed'
                  ? `Sync failed: ${job.error_message || 'Unknown error'}`
                  : 'Processing...',
              }
            }));
            
            // Stop polling if job is complete or failed
            if (['completed', 'failed'].includes(job.status || '')) {
              clearInterval(pollInterval);
              queryClient.invalidateQueries({ queryKey: ['sources', projectId] });
              // Clear progress after 5 seconds
              setTimeout(() => {
                setUploadProgress(prev => {
                  const newProgress = { ...prev };
                  delete newProgress[sourceId];
                  return newProgress;
                });
              }, 5000);
            }
          } catch (error) {
            console.error('Error polling job status:', error);
            // Continue polling on error
          }
        }, 2000);
        
        // Clear interval after 10 minutes (safety timeout)
        setTimeout(() => clearInterval(pollInterval), 600000);
        
      } catch (error: any) {
        const errorMsg = error.response?.data?.detail || error.message || 'Failed to trigger sync';
        alert(`Error: ${errorMsg}`);
      }
    } else {
      // For database sources, use sync endpoint
      await syncMutation.mutateAsync({ sourceId });
    }
  };

  // Group sources by type
  const sourcesByType = React.useMemo(() => {
    const grouped: Record<string, typeof filteredSources> = {};
    filteredSources.forEach(source => {
      const type = source.type || 'unknown';
      if (!grouped[type]) grouped[type] = [];
      grouped[type].push(source);
    });
    return grouped;
  }, [filteredSources]);

  // Stats
  const activeCount = sources?.filter(s => s.sync_status === 'active' || s.sync_status === 'completed').length || 0;
  const failedCount = sources?.filter(s => s.sync_status === 'failed').length || 0;
  const typeCount = Object.keys(sourcesByType).length;

  if (!projectId) {
    return (
      <Card>
        <p className="text-center py-12 text-gray-500 dark:text-gray-300">Select a project to view sources</p>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Database className="h-8 w-8 text-blue-600" />
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">Sources</h1>
            <p className="text-gray-600 dark:text-gray-400 mt-1">
              {filteredSources.length} source{filteredSources.length !== 1 ? 's' : ''} across {typeCount} type{typeCount !== 1 ? 's' : ''}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={() => refetch()}>
            <RefreshCw className="h-4 w-4" />
            Refresh
          </Button>
          <Button size="sm" onClick={() => {
            setSourceType('database');
            setShowAddSource(true);
          }}>
            <Plus className="h-4 w-4" />
            Register Source
          </Button>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border-blue-200 dark:border-blue-800">
          <div className="flex items-center gap-3">
            <Database className="h-8 w-8 text-blue-600 dark:text-blue-400" />
            <div>
              <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">{sources?.length || 0}</div>
              <div className="text-sm text-gray-600 dark:text-gray-400">Total Sources</div>
            </div>
          </div>
        </Card>

        <Card className="bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 border-green-200 dark:border-green-800">
          <div className="flex items-center gap-3">
            <CheckCircle className="h-8 w-8 text-green-600 dark:text-green-400" />
            <div>
              <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">{activeCount}</div>
              <div className="text-sm text-gray-600 dark:text-gray-400">Active</div>
            </div>
          </div>
        </Card>

        <Card className="bg-gradient-to-br from-red-50 to-pink-50 dark:from-red-900/20 dark:to-pink-900/20 border-red-200 dark:border-red-800">
          <div className="flex items-center gap-3">
            <AlertCircle className="h-8 w-8 text-red-600 dark:text-red-400" />
            <div>
              <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">{failedCount}</div>
              <div className="text-sm text-gray-600 dark:text-gray-400">Failed</div>
            </div>
          </div>
        </Card>

        <Card className="bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 border-purple-200 dark:border-purple-800">
          <div className="flex items-center gap-3">
            <Activity className="h-8 w-8 text-purple-600 dark:text-purple-400" />
            <div>
              <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">{typeCount}</div>
              <div className="text-sm text-gray-600 dark:text-gray-400">Source Types</div>
            </div>
          </div>
        </Card>
      </div>

      {/* Search and Filters */}
      <Card>
        <div className="space-y-4">
          <div className="flex gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400 dark:text-gray-300" />
              <Input
                placeholder="Search sources by description, type, or ID..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setPage(0);
                }}
                className="pl-10"
              />
            </div>
          </div>

          <div className="flex gap-4">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                <Filter className="inline h-4 w-4 mr-1" />
                Filter by Type
              </label>
              <select
                value={filterType}
                onChange={(e) => {
                  setFilterType(e.target.value);
                  setPage(0);
                }}
                className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Types</option>
                {Array.from(new Set(sources?.map(s => s.type).filter(Boolean))).map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>

            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Filter by Status
              </label>
              <select
                value={filterStatus}
                onChange={(e) => {
                  setFilterStatus(e.target.value);
                  setPage(0);
                }}
                className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Statuses</option>
                <option value="active">Active</option>
                <option value="completed">Completed</option>
                <option value="pending">Pending</option>
                <option value="failed">Failed</option>
              </select>
            </div>

            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Filter by Parsed
              </label>
              <div className="flex gap-2">
                <select
                  value={filterParsed}
                  onChange={(e) => {
                    setFilterParsed(e.target.value);
                    setPage(0);
                  }}
                  className="flex-1 px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500"
                >
                  <option value="all">All Sources</option>
                  <option value="parsed">Parsed (≥{parsedThreshold}%)</option>
                  <option value="not_parsed">Not Parsed (&lt;{parsedThreshold}%)</option>
                </select>
                {filterParsed !== 'all' && (
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={parsedThreshold}
                    onChange={(e) => {
                      const val = parseInt(e.target.value) || 1;
                      setParsedThreshold(Math.max(0, Math.min(100, val)));
                      setPage(0);
                    }}
                    className="w-20 px-2 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 text-sm"
                    placeholder="%"
                  />
                )}
              </div>
            </div>

            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Sort By
              </label>
              <div className="flex gap-2">
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value as any)}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="last_sync_at">Last Sync</option>
                  <option value="description">Description</option>
                  <option value="type">Type</option>
                </select>
                <button
                  onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                  className="px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-slate-600"
                  title={`Sort ${sortOrder === 'asc' ? 'Descending' : 'Ascending'}`}
                >
                  {sortOrder === 'asc' ? '↑' : '↓'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </Card>

      {/* Sources Grouped by Type */}
      {Object.entries(sourcesByType).map(([type, sourcesOfType]) => (
        <Card key={type} title={`${type} Sources (${sourcesOfType.length})`}>
          <div className="grid grid-cols-1 gap-3">
            {sourcesOfType.slice(page * pageSize, (page + 1) * pageSize).map((source) => {
              const statusColors = {
                active: 'bg-green-100 text-green-800',
                completed: 'bg-blue-100 text-blue-800',
                pending: 'bg-yellow-100 text-yellow-800',
                failed: 'bg-red-100 text-red-800',
              };
              const statusIcons = {
                active: CheckCircle,
                completed: CheckCircle,
                pending: Clock,
                failed: AlertCircle,
              };
              const StatusIcon = statusIcons[source.sync_status as keyof typeof statusIcons] || Activity;

                  const isExpanded = expandedSources.has(source.id);
                  const metadata = source.metadata || {};
                  const fileExtensions = metadata.file_extensions || [];
                  const watchForChanges = metadata.watch_for_changes ?? true;
                  const recursive = metadata.recursive ?? true;
                  const excludePatterns = metadata.exclude_patterns || [];
                  
                  // Get all files from metadata (not limited to 50)
                  const allSourceFiles = source.metadata?.files || source.files || [];
                  
                  return (
                    <div key={source.id} className="border border-gray-200 dark:border-slate-700 rounded-lg hover:shadow-md dark:hover:shadow-lg transition-shadow">
                      <div className="p-4">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-3">
                              {source.type === 'local_folder' ? <Folder className="h-6 w-6 text-blue-600" /> :
                               source.type === 'sharepoint' || source.type === 'gdrive' ? <Cloud className="h-6 w-6 text-blue-600" /> :
                               <Database className="h-6 w-6 text-blue-600" />}
                              <div className="flex-1">
                                <div className="flex items-center gap-2">
                                  <h3 className="font-semibold text-gray-900 dark:text-gray-100">
                                    {source.name || source.description || source.id}
                                  </h3>
                                  <button
                                    onClick={() => {
                                      const newExpanded = new Set(expandedSources);
                                      if (isExpanded) {
                                        newExpanded.delete(source.id);
                                      } else {
                                        newExpanded.add(source.id);
                                      }
                                      setExpandedSources(newExpanded);
                                    }}
                                    className="text-gray-400 dark:text-gray-300 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
                                    title={isExpanded ? 'Collapse details' : 'Expand details'}
                                  >
                                    {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRightIcon className="h-4 w-4" />}
                                  </button>
                                </div>
                                <p className="text-sm text-gray-500 dark:text-gray-300">
                                  {source.file_path && (
                                    <span className="font-mono">{source.file_path}</span>
                                  )}
                                  {!source.file_path && source.uri && (
                                    <span className="font-mono">{source.uri}</span>
                                  )}
                                  {!source.file_path && !source.uri && (
                                    <span className="font-mono">{source.id.substring(0, 24)}...</span>
                                  )}
                                </p>
                                {source.description && source.name && (
                                  <p className="text-xs text-gray-400 dark:text-gray-300 mt-1">{source.description}</p>
                                )}
                              </div>
                            </div>

                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                              <div>
                                <div className="text-gray-500 dark:text-gray-300 text-xs">Type</div>
                                <div className="font-medium text-gray-900 dark:text-gray-100">{source.type}</div>
                              </div>
                              <div>
                                <div className="text-gray-500 dark:text-gray-300 text-xs">Region</div>
                                <div className="font-medium text-gray-900 dark:text-gray-100">{source.source_region || '-'}</div>
                              </div>
                              <div>
                                <div className="text-gray-500 dark:text-gray-400 text-xs">Last Sync</div>
                                <div className="font-medium text-gray-900 dark:text-gray-100">
                                  {source.sync_status === 'queued' && source.last_sync_at
                                    ? 'Queued now'
                                    : source.last_sync_at 
                                    ? new Date(source.last_sync_at).toLocaleString() 
                                    : 'Never'}
                                </div>
                              </div>
                              <div>
                                <div className="text-gray-500 dark:text-gray-400 text-xs">Status</div>
                                <div className="flex items-center gap-1">
                                  <StatusIcon className="h-4 w-4" />
                                  <Badge variant={
                                    source.sync_status === 'active' || source.sync_status === 'completed' ? 'success' :
                                    source.sync_status === 'queued' || source.sync_status === 'pending' ? 'warning' :
                                    source.sync_status === 'running' ? 'default' :
                                    source.sync_status === 'failed' ? 'error' :
                                    'default'
                                  }>
                                    {source.sync_status === 'pending' ? 'queued' : (source.sync_status || 'unknown')}
                                  </Badge>
                                </div>
                              </div>
                            </div>
                            
                            {/* Artifact Statistics for file-based sources */}
                            {(source.type === 'sharepoint' || source.type === 'gdrive' || source.type === 'local_folder') && (
                              <SourceArtifactStats sourceId={source.id} sourceType={source.type} />
                            )}
                            
                            {/* File statistics for file-based sources - Collapsed View */}
                            {source.type === 'local_folder' && (source.file_count ?? 0) > 0 && (
                              <div className="mt-3 pt-3 border-t">
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm mb-3">
                                  <div>
                                    <div className="text-gray-500 dark:text-gray-400 text-xs">Total Files</div>
                                    <div className="font-semibold text-gray-900 dark:text-gray-100 text-lg">{source.file_count}</div>
                                  </div>
                                  <div>
                                    <div className="text-gray-500 dark:text-gray-400 text-xs">Total Size</div>
                                    <div className="font-semibold text-gray-900 dark:text-gray-100 text-lg">
                                      {formatFileSize(source.total_size_bytes ?? 0)}
                                    </div>
                                  </div>
                                  <div>
                                    <div className="text-gray-500 dark:text-gray-400 text-xs">File Types</div>
                                    <div className="font-semibold text-gray-900 dark:text-gray-100 text-lg">
                                      {Object.keys(source.files_by_type || {}).length}
                                    </div>
                                  </div>
                                  <div>
                                    <div className="text-gray-500 dark:text-gray-400 text-xs">Uploaded</div>
                                    <div className="font-semibold text-gray-900 dark:text-gray-100 text-sm flex items-center gap-1">
                                      {source.total_artifact_count !== undefined 
                                        ? <>{source.total_artifact_count.toLocaleString()} artifact{source.total_artifact_count !== 1 ? 's' : ''}</>
                                        : source.artifact_ids && source.artifact_ids.length > 0 
                                        ? `${source.artifact_ids.length} uploaded`
                                        : 'Pending upload'}
                                      {source.has_more_artifacts && (
                                        <Badge variant="default" className="text-xs">
                                          {source.artifact_ids?.length || 0} shown
                                        </Badge>
                                      )}
                                    </div>
                                  </div>
                                </div>
                                
                                {/* Files by Type - Quick View */}
                                {source.files_by_type && Object.keys(source.files_by_type).length > 0 && (
                                  <div className="flex flex-wrap gap-2 mt-2">
                                    {Object.entries(source.files_by_type).map(([type, count]) => (
                                      <Badge key={type} variant="default" className="text-xs">
                                        {type}: {count as number}
                                      </Badge>
                                    ))}
                                  </div>
                                )}
                                
                                {/* Preview of first few files */}
                                {source.files && source.files.length > 0 && (
                                  <div className="mt-3 pt-3 border-t">
                                    <div className="text-gray-500 dark:text-gray-300 text-xs mb-2">Sample Files ({Math.min(3, source.files.length)} of {source.files.length}):</div>
                                    <div className="space-y-1">
                                      {source.files.slice(0, 3).map((file, idx) => (
                                        <div key={idx} className="flex items-center gap-2 text-xs">
                                          <File className="h-3 w-3 text-gray-400 dark:text-gray-300 flex-shrink-0" />
                                          <span className="text-gray-700 dark:text-gray-300 truncate flex-1" title={file.filename}>
                                            {file.filename}
                                          </span>
                                          <span className="text-gray-500 dark:text-gray-300 flex-shrink-0">{formatFileSize(file.file_size_bytes)}</span>
                                        </div>
                                      ))}
                                      {source.files.length > 3 && (
                                        <div className="text-xs text-gray-500 dark:text-gray-300 italic pt-1">
                                          + {source.files.length - 3} more files (expand to see all)
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>

                          {/* Upload Progress Indicator */}
                          {uploadProgress[source.id] && (() => {
                            const progress = uploadProgress[source.id]!;
                            return (
                              <div className="mt-3 pt-3 border-t">
                                <div className="flex items-center gap-3 mb-2">
                                  <Activity className="h-4 w-4 text-blue-600 animate-pulse" />
                                  <div className="flex-1">
                                    <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                      {progress.message}
                                    </div>
                                    {progress.itemsScanned !== undefined && progress.itemsScanned > 0 && (
                                      <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                        Scanned: {progress.itemsScanned.toLocaleString()} items
                                        {progress.filesFound !== undefined && progress.filesFound > 0 && (
                                          <> • Found: {progress.filesFound.toLocaleString()} files</>
                                        )}
                                        {progress.elapsedSeconds !== undefined && (
                                          <> • Elapsed: {Math.floor(progress.elapsedSeconds / 60)}m {Math.floor(progress.elapsedSeconds % 60)}s</>
                                        )}
                                      </div>
                                    )}
                                    {progress.status === 'scanning' && progress.itemsScanned !== undefined && progress.itemsScanned > 0 && (
                                      <div className="mt-2 w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                                        <div 
                                          className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                                          style={{ 
                                            width: `${Math.min(90, (progress.itemsScanned / 100000) * 100)}%` 
                                          }}
                                        />
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            );
                          })()}
                          
                          {/* Actions */}
                          <div className="flex gap-2 ml-4">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleTriggerSync(source.id, source.type, source.metadata, source.name || source.description || source.id)}
                            disabled={syncMutation.isPending || !!uploadProgress[source.id]}
                            title={source.type === 'local_folder' ? 'Trigger file upload' : 'Trigger sync'}
                          >
                            <Play className="h-4 w-4" />
                            {source.type === 'local_folder' ? 'Upload' : 'Sync'}
                          </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={async () => {
                                // Fetch and display sync status inline
                                try {
                                  const status = await getSyncStatus(source.id);
                                  let statusMessage = `Sync Status for ${source.name || source.description || source.id}:\n\n`;
                                  statusMessage += `Type: ${status.source_type}\n`;
                                  statusMessage += `Status: ${status.sync_status}\n`;
                                  statusMessage += `Last Sync: ${status.last_sync_at ? new Date(status.last_sync_at).toLocaleString() : 'Never'}\n`;
                                  
                                  // Show type-specific information
                                  if (source.type === 'local_folder') {
                                    statusMessage += `\nFiles: ${status.total_files ?? 0}\n`;
                                    statusMessage += `Total Size: ${status.total_size ? `${(status.total_size / 1024 / 1024).toFixed(2)} MB` : 'N/A'}\n`;
                                    if (status.files && status.files.length > 0) {
                                      statusMessage += `\nRecent files:\n`;
                                      status.files.slice(0, 5).forEach((file) => {
                                        statusMessage += `  - ${file.file_name} (${file.sync_status})\n`;
                                      });
                                    }
                                  } else if (['postgres', 'mysql', 'mssql'].includes(source.type)) {
                                    statusMessage += `Tables: ${status.total_tables ?? 0}\n`;
                                    statusMessage += `Rows Synced: ${status.total_rows_synced ?? 0}\n`;
                                    statusMessage += `PII Masked: ${status.total_pii_masked ?? 0}\n`;
                                  }
                                  
                                  alert(statusMessage);
                                } catch (error: any) {
                                  const errorMsg = error.response?.data?.detail || error.message || 'Failed to fetch sync status';
                                  alert(`Error: ${errorMsg}`);
                                }
                              }}
                              title="View sync status"
                            >
                              <Activity className="h-4 w-4" />
                              Status
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleDelete(source.id, source.description || source.id, source.artifact_ids?.length || 0)}
                              disabled={deleteMutation.isPending}
                              title="Delete source"
                              className="text-red-600 hover:text-red-700"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                        
                        {/* Expanded Details */}
                        {isExpanded && (
                          <div className="mt-4 pt-4 border-t space-y-4">
                            {/* Source Configuration */}
                            <div>
                              <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Configuration</h4>
                              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                                {source.type === 'local_folder' && (
                                  <>
                                    <div>
                                      <div className="text-gray-500 dark:text-gray-400 text-xs">Watch for Changes</div>
                                      <div className="font-medium text-gray-900 dark:text-gray-100">{watchForChanges ? 'Yes' : 'No'}</div>
                                    </div>
                                    <div>
                                      <div className="text-gray-500 dark:text-gray-400 text-xs">Recursive</div>
                                      <div className="font-medium text-gray-900 dark:text-gray-100">{recursive ? 'Yes' : 'No'}</div>
                                    </div>
                                    <div>
                                      <div className="text-gray-500 dark:text-gray-400 text-xs">File Extensions</div>
                                      <div className="font-medium text-gray-900 dark:text-gray-100">{fileExtensions.length} selected</div>
                                    </div>
                                    <div>
                                      <div className="text-gray-500 dark:text-gray-400 text-xs">Exclude Patterns</div>
                                      <div className="font-medium text-gray-900 dark:text-gray-100">{excludePatterns.length} patterns</div>
                                    </div>
                                  </>
                                )}
                                <div>
                                  <div className="text-gray-500 dark:text-gray-400 text-xs">Created</div>
                                  <div className="font-medium text-gray-900 dark:text-gray-100">
                                    {new Date(source.created_at).toLocaleString()}
                                  </div>
                                </div>
                              </div>
                              
                              {/* File Extensions */}
                              {fileExtensions.length > 0 && (
                                <div className="mt-3">
                                  <div className="text-gray-500 dark:text-gray-300 text-xs mb-2">Selected File Extensions:</div>
                                  <div className="flex flex-wrap gap-2">
                                    {fileExtensions.map((ext: string) => (
                                      <Badge key={ext} variant="default" className="text-xs">
                                        {ext} {EXTENSION_DESCRIPTIONS[ext] ? `(${EXTENSION_DESCRIPTIONS[ext]})` : ''}
                                      </Badge>
                                    ))}
                                  </div>
                                </div>
                              )}
                              
                              {/* Exclude Patterns */}
                              {excludePatterns.length > 0 && (
                                <div className="mt-3">
                                  <div className="text-gray-500 dark:text-gray-300 text-xs mb-2">Exclude Patterns:</div>
                                  <div className="flex flex-wrap gap-2">
                                    {excludePatterns.map((pattern: string, idx: number) => (
                                      <Badge key={idx} variant="default" className="text-xs font-mono border border-gray-300">
                                        {pattern}
                                      </Badge>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                            
                            {/* File List / File Tree / Statistics Tabs */}
                            {source.type === 'local_folder' && allSourceFiles.length > 0 && (
                              <SourceFileTabs
                                sourceId={source.id}
                                files={allSourceFiles}
                                currentTab={sourceViewTabs[source.id] || 'list'}
                                onTabChange={(tab) => setSourceViewTabs(prev => ({ ...prev, [source.id]: tab }))}
                                fileListPage={fileListPages[source.id] || 0}
                                onFileListPageChange={(page) => setFileListPages(prev => ({ ...prev, [source.id]: page }))}
                                fileListPageSize={fileListPageSize}
                                expandedTreePaths={sourceExpandedPaths[source.id] || new Set()}
                                onExpandedTreePathsChange={(updater) => {
                                  setSourceExpandedPaths(prev => {
                                    const newPaths = typeof updater === 'function' 
                                      ? updater(prev[source.id] || new Set()) 
                                      : updater;
                                    return { ...prev, [source.id]: newPaths };
                                  });
                                }}
                                buildFileTree={buildFileTreeMemo}
                                formatFileSize={formatFileSize}
                                supportedExtensions={SUPPORTED_EXTENSIONS}
                                extensionDescriptions={EXTENSION_DESCRIPTIONS}
                                fileExtensions={fileExtensions}
                                source={source}
                              />
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
          </div>
        </Card>
      ))}

      {filteredSources.length === 0 && !isLoading && (
        <Card>
          <div className="text-center py-12">
            <Database className="h-12 w-12 text-gray-400 dark:text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 dark:text-gray-300 mb-4">No sources configured</p>
            <Button onClick={() => setShowAddSource(true)}>
              <Plus className="h-4 w-4" />
              Register Your First Source
            </Button>
          </div>
        </Card>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <Card>
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-600 dark:text-gray-400">
              Showing {page * pageSize + 1}-{Math.min((page + 1) * pageSize, filteredSources.length)} of {filteredSources.length}
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.max(0, p - 1))}
                disabled={page === 0}
              >
                <ChevronLeft className="h-4 w-4" />
                Previous
              </Button>
              <div className="flex gap-1">
                {Array.from({ length: totalPages }, (_, i) => i).map((pageNum) => (
                  <button
                    key={pageNum}
                    onClick={() => setPage(pageNum)}
                    className={`px-3 py-1 rounded ${
                      page === pageNum
                        ? 'bg-blue-600 dark:bg-blue-500 text-white'
                        : 'bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-slate-600'
                    }`}
                  >
                    {pageNum + 1}
                  </button>
                ))}
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                disabled={page === totalPages - 1}
              >
                Next
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* Add Source Modal */}
      {showAddSource && (
        <AddSourceModal
          projectId={projectId!}
          sourceType={sourceType}
          onSourceTypeChange={setSourceType}
          onClose={() => {
                      setShowAddSource(false);
            setSourceType('database');
          }}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ['sources', projectId] });
            setShowAddSource(false);
            setSourceType('database');
          }}
        />
      )}

      {/* Delete Confirmation Modal */}
      <DeleteSourceConfirmationModal
        sourceName={deleteConfirmSource?.name || ''}
        artifactCount={deleteConfirmSource?.artifactCount || 0}
        isOpen={!!deleteConfirmSource}
        onClose={() => setDeleteConfirmSource(null)}
        onConfirm={confirmDelete}
        isDeleting={deleteMutation.isPending}
      />

      {/* Bulk Upload Modal */}
      {bulkUploadSource && (() => {
        const source = sources?.find(s => s.id === bulkUploadSource.id);
        
        // Get all files from metadata (not limited to 50 like the API response)
        // The metadata.files array contains all files from registration
        const allFilesFromMetadata = source?.metadata?.files || [];
        
        // Note: If source has many artifacts (indicated by has_more_artifacts),
        // the metadata.files should contain the full list from registration.
        // The artifact_ids array is paginated for performance.
        
        // If metadata doesn't have files, fall back to the API response (limited to 50)
        const filesToUse = allFilesFromMetadata.length > 0 
          ? allFilesFromMetadata 
          : (source?.files || []);
        
        // Merge with uploaded artifacts to get artifact_ids
        const filesWithArtifacts = filesToUse.map((fileData: any) => {
          // Check if this file has been uploaded by matching filename
          const uploadedFile = source?.files?.find(f => 
            f.filename === (fileData.filename || fileData.name)
          );
          return {
            filename: fileData.filename || fileData.name || '',
            path: fileData.path || fileData.filename || fileData.name || '',
            file_size_bytes: fileData.file_size_bytes || fileData.size || 0,
            artifact_type: fileData.artifact_type || 'unknown',
            artifact_id: uploadedFile?.artifact_id || fileData.artifact_id || null,
            created_at: uploadedFile?.created_at || fileData.created_at || null,
          };
        });
        
        return (
          <BulkUploadModal
            projectId={projectId!}
            sourceId={bulkUploadSource.id}
            sourceName={bulkUploadSource.name}
            sourceFiles={filesWithArtifacts}
            sourceMetadata={bulkUploadSource.metadata}
            onClose={() => setBulkUploadSource(null)}
          />
        );
      })()}
    </div>
  );
}
